import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import ChartContent from 'charts/chart-content/ChartContent';
import {ChartNode, Chart} from 'charts/ChartTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import DataFrameTable, {DataFrameRenderOverrides} from 'data-frames/table/DataFrameTable';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import isSystemColumn from 'data-frames/util/isSystemColumn';
import sampleRows from 'data-frames/util/sampleRows';
import VisualizationConfigEditor from 'insights/analytics-visualizations/configs/VisualizationConfigEditor';
import VisualizationViewer from 'insights/analytics-visualizations/viewers/VisualizationViewer';
import {PANE_MARGIN} from 'insights/InsightsConstants';
import convertDisplayNameBasedConfigs from 'insights/universal-blocks/convertDisplayNameBasedConfigs';
import {ExperimentalShapashTableOptions} from 'machine-learning/handleClickShapashTableRow';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';
import Pane from 'ui-library/pane/Pane';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';
import {listToReadable} from 'util/NameUtil';

import {BlockViewContext} from './BlockViewContext';
import {useRawOutputButton} from './components/RawOutputButton';
import {SharedUniversalBlockViewProps} from './SharedUniversalBlockViewTypes';
import {VisualizationConfig} from './SharedUniversalBlockViewTypes';

export const SAMPLED_DATASET_MESSAGE = 'Showing sampled dataset';

interface EnabledConfigEditorSettings {
  showConfigEditor: true;
  onChangeVisualizationConfig: (newVisualizationConfig: VisualizationConfig) => void;
}

interface DisabledConfigEditorSettings {
  showConfigEditor: false;
}

export type VisualizationConfigEditorSettings = EnabledConfigEditorSettings | DisabledConfigEditorSettings;

export interface ErrorHandlingOptions {
  errorMessage?: string | null;
  showVisualization: boolean;
}

export interface PresentationalUniversalBlockViewProps extends SharedUniversalBlockViewProps {
  actionOrigin: ActionOrigin;
  // Core block props
  configEditorSettings?: VisualizationConfigEditorSettings;
  errorHandlingOptions?: ErrorHandlingOptions | null;
  inputDataFrame: DataFrame;
  maxRows?: number;
  renderOptions?: {
    delayExpensiveRenders?: boolean;
    isRawOutputEnabled?: boolean;
  };
  viewStateId: string;
  visualizationConfig: VisualizationConfig | null;
  regressionAnalyses?: Array<SuccessfulAnalysis<RegressionAnalysis>>;

  // If true, we will only render charts using ChartContent and the chart prop, if false, we will render by
  // passing visualizationConfig to VisualizationViewer
  renderFromChart?: boolean;
  chart?: ChartNode | Chart | null;

  // Should only be used and set to true by Insights. This prop defines the enablement of some legacy functionality
  // of PresentationalUniversalBlockView that is only used by Insights. All this functionality should eventually
  // be deprecated and removed.
  isLegacyInsights?: boolean;

  dataFrameRenderOverrides?: DataFrameRenderOverrides;

  // Experimental props
  experimentalShapashTableOptions?: ExperimentalShapashTableOptions;
}

/**
 * Core presentational component of a Universal Block
 *
 * This component acts as the visual front-end of a Universal Block, and can also be rendered on its own as
 * a front-end-only Universal Block.
 *
 * This component is "presentational" in the sense that it does not call to back-end state, but it does have some
 * persistence in Redux to maintain its component state through component remounts. It currently also does some
 * back-end calls to stateless tasks to fit regression curves.
 *
 * Since this component can also be used independently as a front-end-only Universal Block, the props are
 * generalized for the caller's convenience, as opposed to taking a back-end UniversalBlock data model.
 */
export default function PresentationalUniversalBlockView({
  actionOrigin,
  configEditorSettings = {showConfigEditor: false},
  errorHandlingOptions,
  experimentalShapashTableOptions,
  inputDataFrame,
  dataFrameRenderOverrides,
  isLegacyInsights = false,
  paneProps: {toolbar, ...paneProps},
  chart,
  renderFromChart = false,
  renderOptions: {delayExpensiveRenders = false, isRawOutputEnabled = false} = {},
  viewStateId,
  visualizationConfig,
  regressionAnalyses,
  maxRows,
}: PresentationalUniversalBlockViewProps): JSX.Element {
  assert(
    visualizationConfig != null || (renderFromChart && chart != null),
    'visualizationConfig required if not rendering from chart'
  );
  assertAndContinue(
    isLegacyInsights === (actionOrigin === ActionOrigin.INSIGHTS),
    'Expected isLegacyInsights to only be true when used in Insights',
    {actionOrigin, isLegacyInsights}
  );
  const {rawOutputButton, showRawOutput} = useRawOutputButton(viewStateId);
  const dataFrameTableSortOptions = useSimpleTableSortState(viewStateId);

  const {visualizationConfig: updatedVisualizationConfig, missingColumns} = useMemo(() => {
    if (!isLegacyInsights || visualizationConfig == null) {
      return {visualizationConfig, missingColumns: []};
    }

    const {parameters: systemNameBasedParameters, missingColumns} = convertDisplayNameBasedConfigs(
      inputDataFrame,
      visualizationConfig.parameters
    );

    return {
      visualizationConfig: {
        ...visualizationConfig,
        parameters: systemNameBasedParameters,
      },
      missingColumns,
    };
  }, [inputDataFrame, visualizationConfig, isLegacyInsights]);

  const maybeSampledDataFrame = useMemo(
    () =>
      maxRows == null || inputDataFrame.rows.length < maxRows
        ? inputDataFrame
        : sampleRows(inputDataFrame, maxRows),
    [inputDataFrame, maxRows]
  );

  if (inputDataFrame.rows.length > maybeSampledDataFrame.rows.length) {
    paneProps.warnings = [...(paneProps.warnings ?? []), SAMPLED_DATASET_MESSAGE];
  }

  const errorMessage =
    errorHandlingOptions?.errorMessage ??
    (missingColumns.length > 0
      ? `Missing ${pluralize(missingColumns.length, 'column')} in query output: ${listToReadable(
          missingColumns
        )}`
      : null);

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
    <BlockViewContext.Provider value={{actionOrigin, viewStateId}}>
      <Pane
        {...paneProps}
        centerContent={false}
        sidebar={
          configEditorSettings.showConfigEditor ? (
            <VisualizationConfigEditor
              columns={inputDataFrame.columns.filter((column) => !isSystemColumn(column))}
              onChangeVisualizationConfig={configEditorSettings.onChangeVisualizationConfig}
              visualizationConfig={visualizationConfig}
              useSystemNames={!isLegacyInsights}
            />
          ) : undefined
        }
        toolbar={
          <>
            {isRawOutputEnabled && <span className="u-mini-spacing-left">{rawOutputButton}</span>}
            {toolbar}
          </>
        }
      >
        {showRawOutput && isRawOutputEnabled ? (
          <DataFrameTable
            actionOrigin={actionOrigin}
            dataFrame={inputDataFrame}
            clientStateId={viewStateId}
            mode={DataFrameTableMode.RAW_WAREHOUSE}
            renderIsExcludedColumn={false}
            selectionOptions={{allowRowSelection: false}}
            sortOptions={dataFrameTableSortOptions}
            dataFrameRenderOverrides={dataFrameRenderOverrides}
          />
        ) : (
          <>
            {errorMessage != null && (
              <div className={css(styles.errorBanner)}>
                <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
              </div>
            )}
            {renderFromChart &&
              chart != null &&
              (errorMessage == null || errorHandlingOptions?.showVisualization) && (
                <ChartContent chart={chart} />
              )}
            {!renderFromChart && (errorMessage == null || errorHandlingOptions?.showVisualization) && (
              <VisualizationViewer
                dataFrame={maybeSampledDataFrame}
                delayExpensiveRenders={delayExpensiveRenders}
                experimentalShapashTableOptions={experimentalShapashTableOptions}
                isLegacyInsights={isLegacyInsights}
                // We've already asserted that visualizationConfig can't be null when renderFromChart is false
                visualization={updatedVisualizationConfig!}
                regressionAnalyses={regressionAnalyses}
              />
            )}
          </>
        )}
      </Pane>
    </BlockViewContext.Provider>
  );
}

export const styles = StyleSheet.create({
  errorBanner: {
    margin: PANE_MARGIN,
  },
});
